import {
    Badge,
    Box,
    Code,
    Divider,
    Flex,
    Heading,
    Icon,
    Link,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
} from "@chakra-ui/react";
import { FaBook, FaWhatsapp } from "react-icons/fa";
import { TbArrowRight, TbKey, TbCloudDataConnection } from "react-icons/tb";

function InlineCode({ children, ...props }) {
    const bg = useColorModeValue("gray.100", "gray.600");
    const color = useColorModeValue("purple.600", "purple.200");
    return (
        <Code bg={bg} color={color} px={1.5} py={0.5} borderRadius="md" fontSize="inherit" {...props}>
            {children}
        </Code>
    );
}

function EndpointSection({ method, path, description, children }) {
    const codeBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");

    const methodColors = {
        GET: "green",
        POST: "blue",
        PUT: "orange",
        DELETE: "red",
    };

    return (
        <Box
            border="1px solid"
            borderColor={borderColor}
            borderRadius="lg"
            overflow="hidden"
            mb={4}
        >
            <Flex
                bg={codeBg}
                p={3}
                align="center"
                gap={3}
                borderBottom="1px solid"
                borderColor={borderColor}
            >
                <Badge
                    colorScheme={methodColors[method] || "gray"}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontFamily="mono"
                >
                    {method}
                </Badge>
                <InlineCode fontSize="sm" bg="transparent" fontWeight="semibold">
                    {path}
                </InlineCode>
            </Flex>
            <Box p={4} fontSize="sm">
                <Text mb={3} color="gray.600" _dark={{ color: "gray.400" }}>
                    {description}
                </Text>
                {children}
            </Box>
        </Box>
    );
}

function ResponseField({ name, type, description, example }) {
    return (
        <Tr>
            <Td>
                <InlineCode fontSize="xs">{name}</InlineCode>
            </Td>
            <Td>
                <Badge variant="outline" fontSize="xs">{type}</Badge>
            </Td>
            <Td>{description}</Td>
            <Td>
                <InlineCode fontSize="xs">{example}</InlineCode>
            </Td>
        </Tr>
    );
}

function IntegrationDocs() {
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const sectionBg = useColorModeValue("gray.50", "gray.700");
    const dividerColor = useColorModeValue("gray.200", "gray.500");

    return (
        <Box maxW="1000px" mx="auto" px={{ base: 4, md: 6 }} py={8} fontSize="sm">
            {/* Header */}
            <Box
                bg={cardBg}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                border="1px solid"
                borderColor={borderColor}
                mb={8}
            >
                <Box bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)" py={10} px={8} textAlign="center">
                    <Flex align="center" justify="center" bg="whiteAlpha.200" borderRadius="full" p={4} mb={4} mx="auto" w="fit-content">
                        <Icon as={FaBook} color="white" boxSize={8} />
                    </Flex>
                    <Heading color="white" size="lg" mb={2}>
                        API de Integração
                    </Heading>
                    <Text color="whiteAlpha.800" fontSize="md">
                        WS Consultoria Climática
                    </Text>
                </Box>

                <Box p={8}>
                    <Text mb={4} lineHeight="tall">
                        A API de Integração permite que sistemas externos acessem dados meteorológicos
                        das estações disponíveis na plataforma WS Clima. Com ela, você pode listar estações,
                        consultar dados em tempo real e acessar dados históricos.
                    </Text>

                    <Box bg="blue.50" _dark={{ bg: "blue.900" }} p={4} borderRadius="lg" mb={4}>
                        <Flex align="center" gap={2} mb={2}>
                            <Icon as={TbKey} color="blue.500" />
                            <Text fontWeight="bold" color="blue.700" _dark={{ color: "blue.200" }}>
                                Como obter acesso
                            </Text>
                        </Flex>
                        <Text color="blue.700" _dark={{ color: "blue.200" }} mb={3}>
                            O acesso à API é feito através de uma chave (token) fornecida pela equipe
                            do WS Clima. Cada chave tem permissões específicas de estações.
                        </Text>
                        <Link
                            href="https://wa.me/553591851089?text=Ol%C3%A1%21%20Gostaria%20de%20obter%20acesso%20%C3%A0%20API%20de%20integra%C3%A7%C3%A3o%20do%20WS%20Clima"
                            isExternal
                            bg="whatsapp.500"
                            color="white"
                            px={4}
                            py={2}
                            borderRadius="lg"
                            fontWeight="semibold"
                            display="inline-flex"
                            alignItems="center"
                            gap={2}
                            _hover={{ bg: "whatsapp.600", textDecoration: "none" }}
                        >
                            <Icon as={FaWhatsapp} /> Solicitar acesso — William (35) 9185-1089
                        </Link>
                    </Box>
                </Box>
            </Box>

            {/* Authentication */}
            <Box
                bg={cardBg}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="md"
                border="1px solid"
                borderColor={borderColor}
                mb={8}
            >
                <Box bg={sectionBg} py={3} px={6} borderBottom="1px solid" borderColor={borderColor}>
                    <Heading size="sm">Autenticação</Heading>
                </Box>
                <Box p={6}>
                    <Text mb={4}>
                        Todas as requisições devem incluir o token de API no cabeçalho <InlineCode>Authorization</InlineCode> utilizando o esquema Bearer:
                    </Text>
                    <Box bg="gray.900" color="green.300" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" mb={4} overflowX="auto">
                        <Text>Authorization: Bearer <Text as="span" color="yellow.300">seu_token_aqui</Text></Text>
                    </Box>

                    <Heading size="xs" mb={2} mt={6}>URL Base</Heading>
                    <Box bg="gray.900" color="green.300" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" mb={4} overflowX="auto">
                        <Text>https://api.wsclima.com.br/integrations</Text>
                    </Box>

                    <Heading size="xs" mb={2} mt={6}>Códigos de Erro</Heading>
                    <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }}>
                        <Thead>
                            <Tr>
                                <Th>Código</Th>
                                <Th>Descrição</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr><Td><InlineCode>400</InlineCode></Td><Td>Parâmetros inválidos (formato de data, intervalo, etc.)</Td></Tr>
                            <Tr><Td><InlineCode>401</InlineCode></Td><Td>Token ausente, inválido ou expirado</Td></Tr>
                            <Tr><Td><InlineCode>404</InlineCode></Td><Td>Estação não encontrada ou sem permissão de acesso</Td></Tr>
                            <Tr><Td><InlineCode>429</InlineCode></Td><Td>Limite de requisições excedido</Td></Tr>
                        </Tbody>
                    </Table></Box>
                </Box>
            </Box>

            {/* Endpoints */}
            <Box
                bg={cardBg}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="md"
                border="1px solid"
                borderColor={borderColor}
                mb={8}
            >
                <Box bg={sectionBg} py={3} px={6} borderBottom="1px solid" borderColor={borderColor}>
                    <Flex align="center" gap={2}>
                        <Icon as={TbCloudDataConnection} />
                        <Heading size="sm">Endpoints</Heading>
                    </Flex>
                </Box>
                <Box p={6}>

                    {/* List Stations */}
                    <EndpointSection
                        method="GET"
                        path="/stations/list"
                        description="Lista todas as estações disponíveis para a sua chave de API. Não recebe parâmetros."
                    >
                        <Text fontWeight="semibold" mb={2}>Resposta:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }}>
                            <Thead><Tr><Th>Campo</Th><Th>Tipo</Th><Th>Descrição</Th><Th>Exemplo</Th></Tr></Thead>
                            <Tbody>
                                <ResponseField name="count" type="integer" description="Total de estações" example="2" />
                                <ResponseField name="stations[].id" type="integer" description="ID interno da estação" example="2" />
                                <ResponseField name="stations[].identifier" type="string" description="Identificador do provedor" example="AA:BB:CC:DD:EE:FF" />
                                <ResponseField name="stations[].provider" type="string" description="Provedor da estação" example="WS Clima" />
                                <ResponseField name="stations[].name" type="string" description="Nome/descrição da estação" example="Morada dos Canyons" />
                                <ResponseField name="stations[].city" type="string" description="Cidade" example="Praia Grande" />
                                <ResponseField name="stations[].state" type="string" description="Estado (UF)" example="SC" />
                                <ResponseField name="stations[].online" type="boolean" description="Se a estação está online" example="true" />
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mt={4} mb={2}>Exemplo de resposta:</Text>
                        <Box bg="gray.900" color="gray.100" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" overflowX="auto" whiteSpace="pre">
{`{
  "count": 2,
  "stations": [
    {
      "id": 2,
      "identifier": "AA:BB:CC:DD:EE:FF",
      "provider": "WS Clima",
      "name": "Morada dos Canyons",
      "city": "Praia Grande",
      "state": "SC",
      "online": true
    },
    {
      "id": 8,
      "identifier": "A826",
      "provider": "INMET",
      "name": "Alegrete - RS",
      "city": "Alegrete",
      "state": "RS",
      "online": false
    }
  ]
}`}
                        </Box>
                    </EndpointSection>

                    <Divider my={6} borderColor={dividerColor} />

                    {/* Station Details */}
                    <EndpointSection
                        method="GET"
                        path="/stations/{id}/details"
                        description="Retorna os detalhes de uma estação específica."
                    >
                        <Text fontWeight="semibold" mb={2}>Parâmetros da URL:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }} mb={4}>
                            <Thead><Tr><Th>Parâmetro</Th><Th>Tipo</Th><Th>Obrigatório</Th><Th>Descrição</Th></Tr></Thead>
                            <Tbody>
                                <Tr>
                                    <Td><InlineCode fontSize="xs">id</InlineCode></Td>
                                    <Td><Badge variant="outline" fontSize="xs">string | integer</Badge></Td>
                                    <Td><Badge colorScheme="red" fontSize="xs">Sim</Badge></Td>
                                    <Td>ID numérico interno da estação ou o identificador textual do provedor (ex: <InlineCode fontSize="xs">42</InlineCode> ou <InlineCode fontSize="xs">IPARANA15</InlineCode>)</Td>
                                </Tr>
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mb={2}>Resposta:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }}>
                            <Thead><Tr><Th>Campo</Th><Th>Tipo</Th><Th>Descrição</Th><Th>Exemplo</Th></Tr></Thead>
                            <Tbody>
                                <ResponseField name="id" type="integer" description="ID interno" example="2" />
                                <ResponseField name="identifier" type="string" description="Identificador do provedor" example="AA:BB:CC:DD:EE:FF" />
                                <ResponseField name="provider" type="string" description="Provedor" example="WS Clima" />
                                <ResponseField name="name" type="string" description="Nome da estação" example="Morada dos Canyons" />
                                <ResponseField name="city" type="string" description="Cidade" example="Praia Grande" />
                                <ResponseField name="state" type="string" description="Estado (UF)" example="SC" />
                                <ResponseField name="lat" type="decimal" description="Latitude" example="-29.156703" />
                                <ResponseField name="long" type="decimal" description="Longitude" example="-49.875302" />
                                <ResponseField name="elevation" type="decimal" description="Elevação em metros" example="17" />
                                <ResponseField name="timezone" type="string" description="Fuso horário" example="America/Sao_Paulo" />
                                <ResponseField name="online" type="boolean" description="Status online/offline" example="true" />
                                <ResponseField name="last_sync_at" type="ISO 8601" description="Data/hora da última sincronização" example="2026-02-16T18:12:12-03:00" />
                                <ResponseField name="data_since" type="date" description="Data mais antiga com dados disponíveis" example="2025-04-26" />
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mt={4} mb={2}>Exemplo de resposta:</Text>
                        <Box bg="gray.900" color="gray.100" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" overflowX="auto" whiteSpace="pre">
{`{
  "id": 2,
  "identifier": "AA:BB:CC:DD:EE:FF",
  "provider": "WS Clima",
  "name": "Morada dos Canyons",
  "city": "Praia Grande",
  "state": "SC",
  "lat": -29.156703,
  "long": -49.875302,
  "elevation": 17,
  "timezone": "America/Sao_Paulo",
  "online": true,
  "last_sync_at": "2026-02-16T18:12:12-03:00",
  "data_since": "2025-04-26"
}`}
                        </Box>
                    </EndpointSection>

                    <Divider my={6} borderColor={dividerColor} />

                    {/* Current Data */}
                    <EndpointSection
                        method="GET"
                        path="/stations/{id}/data/current"
                        description="Retorna a última leitura da estação (dados em tempo real)."
                    >
                        <Text fontWeight="semibold" mb={2}>Parâmetros da URL:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }} mb={4}>
                            <Thead><Tr><Th>Parâmetro</Th><Th>Tipo</Th><Th>Obrigatório</Th><Th>Descrição</Th></Tr></Thead>
                            <Tbody>
                                <Tr>
                                    <Td><InlineCode fontSize="xs">id</InlineCode></Td>
                                    <Td><Badge variant="outline" fontSize="xs">string | integer</Badge></Td>
                                    <Td><Badge colorScheme="red" fontSize="xs">Sim</Badge></Td>
                                    <Td>ID numérico interno ou identificador do provedor</Td>
                                </Tr>
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mb={2}>Resposta (campo <InlineCode>data</InlineCode>):</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }}>
                            <Thead><Tr><Th>Campo</Th><Th>Tipo</Th><Th>Descrição</Th><Th>Exemplo</Th></Tr></Thead>
                            <Tbody>
                                <ResponseField name="temp" type="decimal" description="Temperatura (°C)" example="23.1" />
                                <ResponseField name="humidity" type="integer" description="Umidade relativa (%)" example="95" />
                                <ResponseField name="heat_index" type="decimal" description="Índice de calor (°C)" example="23.1" />
                                <ResponseField name="dew_point" type="decimal" description="Ponto de orvalho (°C)" example="22.3" />
                                <ResponseField name="wind_chill" type="decimal" description="Sensação térmica (°C)" example="null" />
                                <ResponseField name="wind_speed" type="decimal" description="Velocidade do vento (km/h)" example="2.5" />
                                <ResponseField name="wind_gust" type="decimal" description="Rajada de vento (km/h)" example="3.6" />
                                <ResponseField name="wind_dir" type="integer" description="Direção do vento (graus)" example="182" />
                                <ResponseField name="pressure" type="decimal" description="Pressão atmosférica (hPa)" example="962.7" />
                                <ResponseField name="precip_rate" type="decimal" description="Taxa de precipitação (mm/h)" example="0.0" />
                                <ResponseField name="precip_total" type="decimal" description="Precipitação acumulada no dia (mm)" example="1.0" />
                                <ResponseField name="uv" type="decimal" description="Índice UV" example="0.0" />
                                <ResponseField name="solar_radiation" type="decimal" description="Radiação solar (W/m²)" example="0.0" />
                                <ResponseField name="created_at" type="ISO 8601" description="Data/hora da leitura (fuso local)" example="2026-02-16T21:12:12-03:00" />
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mt={4} mb={2}>Exemplo de resposta:</Text>
                        <Box bg="gray.900" color="gray.100" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" overflowX="auto" whiteSpace="pre">
{`{
  "station_id": 2,
  "data": {
    "temp": 23.1,
    "humidity": 95,
    "heat_index": 23.1,
    "dew_point": 22.3,
    "wind_chill": null,
    "wind_speed": 2.5,
    "wind_gust": 3.6,
    "wind_dir": 182,
    "pressure": 962.7,
    "precip_rate": 0.0,
    "precip_total": 1.0,
    "uv": 0.0,
    "solar_radiation": 0.0,
    "created_at": "2026-02-16T21:12:12-03:00"
  }
}`}
                        </Box>
                    </EndpointSection>

                    <Divider my={6} borderColor={dividerColor} />

                    {/* Historical Data */}
                    <EndpointSection
                        method="GET"
                        path="/stations/{id}/data/historical/{start}/{end}"
                        description="Retorna dados históricos para um período específico."
                    >
                        <Text fontWeight="semibold" mb={2}>Parâmetros da URL:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }} mb={4}>
                            <Thead><Tr><Th>Parâmetro</Th><Th>Tipo</Th><Th>Obrigatório</Th><Th>Descrição</Th></Tr></Thead>
                            <Tbody>
                                <Tr>
                                    <Td><InlineCode fontSize="xs">id</InlineCode></Td>
                                    <Td><Badge variant="outline" fontSize="xs">string | integer</Badge></Td>
                                    <Td><Badge colorScheme="red" fontSize="xs">Sim</Badge></Td>
                                    <Td>ID numérico interno ou identificador do provedor</Td>
                                </Tr>
                                <Tr>
                                    <Td><InlineCode fontSize="xs">start</InlineCode></Td>
                                    <Td><Badge variant="outline" fontSize="xs">string</Badge></Td>
                                    <Td><Badge colorScheme="red" fontSize="xs">Sim</Badge></Td>
                                    <Td>Data de início no formato <InlineCode fontSize="xs">YYYYMMDD</InlineCode> (ex: <InlineCode fontSize="xs">20260301</InlineCode>)</Td>
                                </Tr>
                                <Tr>
                                    <Td><InlineCode fontSize="xs">end</InlineCode></Td>
                                    <Td><Badge variant="outline" fontSize="xs">string</Badge></Td>
                                    <Td><Badge colorScheme="red" fontSize="xs">Sim</Badge></Td>
                                    <Td>Data de fim no formato <InlineCode fontSize="xs">YYYYMMDD</InlineCode> (ex: <InlineCode fontSize="xs">20260306</InlineCode>). Intervalo máximo de 366 dias.</Td>
                                </Tr>
                            </Tbody>
                        </Table></Box>

                        <Box bg="orange.50" _dark={{ bg: "orange.900" }} p={3} borderRadius="md" mb={4}>
                            <Text fontWeight="semibold" color="orange.700" _dark={{ color: "orange.200" }} mb={1}>
                                Agrupamento automático
                            </Text>
                            <Text fontSize="xs" color="orange.600" _dark={{ color: "orange.300" }}>
                                Os dados são automaticamente agrupados conforme o período solicitado para otimizar a resposta:
                            </Text>
                            <Flex direction="column" mt={2} gap={1}>
                                <Flex align="center" gap={2} fontSize="xs" color="orange.700" _dark={{ color: "orange.200" }}>
                                    <Icon as={TbArrowRight} /> <b>Até 7 dias:</b> dados brutos (todas as leituras)
                                </Flex>
                                <Flex align="center" gap={2} fontSize="xs" color="orange.700" _dark={{ color: "orange.200" }}>
                                    <Icon as={TbArrowRight} /> <b>8 a 31 dias:</b> agrupados a cada 4 horas (min/avg/max)
                                </Flex>
                                <Flex align="center" gap={2} fontSize="xs" color="orange.700" _dark={{ color: "orange.200" }}>
                                    <Icon as={TbArrowRight} /> <b>Mais de 31 dias:</b> agrupados por dia (min/avg/max)
                                </Flex>
                            </Flex>
                        </Box>

                        <Text fontWeight="semibold" mb={2}>Resposta:</Text>
                        <Box overflowX="auto"><Table size="sm" variant="simple" sx={{ "td, th": { borderColor: dividerColor } }}>
                            <Thead><Tr><Th>Campo</Th><Th>Tipo</Th><Th>Descrição</Th><Th>Exemplo</Th></Tr></Thead>
                            <Tbody>
                                <ResponseField name="period.start" type="date" description="Data de início" example="2026-03-01" />
                                <ResponseField name="period.end" type="date" description="Data de fim" example="2026-03-06" />
                                <ResponseField name="period.days" type="integer" description="Número de dias" example="5" />
                                <ResponseField name="period.grouping" type="string" description="Tipo de agrupamento: raw, 4hours ou 1day" example="raw" />
                                <ResponseField name="count" type="integer" description="Quantidade de registros retornados" example="284" />
                                <ResponseField name="data[]" type="array" description="Lista de leituras/agregações" example="[...]" />
                            </Tbody>
                        </Table></Box>

                        <Text fontWeight="semibold" mt={4} mb={2}>
                            Campos de dados (modo raw):
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                            Mesmos campos do endpoint de dados atuais (temp, humidity, wind_speed, etc.)
                        </Text>

                        <Text fontWeight="semibold" mt={4} mb={2}>
                            Campos de dados (modo agrupado):
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                            Cada campo possui as variantes <InlineCode fontSize="xs">_min</InlineCode>, <InlineCode fontSize="xs">_avg</InlineCode> e <InlineCode fontSize="xs">_max</InlineCode> (ex: temp_min, temp_avg, temp_max).
                            Campo <InlineCode fontSize="xs">period</InlineCode> indica o início do intervalo em ISO 8601.
                        </Text>

                        <Text fontWeight="semibold" mt={4} mb={2}>Exemplo de resposta (modo raw — até 7 dias):</Text>
                        <Box bg="gray.900" color="gray.100" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" overflowX="auto" whiteSpace="pre">
{`// GET /stations/2/data/historical/20260214/20260216
{
  "station_id": 2,
  "period": {
    "start": "2026-02-14",
    "end": "2026-02-16",
    "days": 2,
    "grouping": "raw"
  },
  "count": 284,
  "data": [
    {
      "temp": 23.1,
      "humidity": 95,
      "heat_index": 23.1,
      "dew_point": 22.3,
      "wind_chill": null,
      "wind_speed": 2.5,
      "wind_gust": 3.6,
      "wind_dir": 182,
      "pressure": 962.7,
      "precip_rate": 0.0,
      "precip_total": 1.0,
      "uv": 0.0,
      "solar_radiation": 0.0,
      "created_at": "2026-02-14T00:05:00-03:00"
    },
    ...
  ]
}`}
                        </Box>

                        <Text fontWeight="semibold" mt={4} mb={2}>Exemplo de resposta (modo agrupado — 8+ dias):</Text>
                        <Box bg="gray.900" color="gray.100" p={4} borderRadius="lg" fontFamily="mono" fontSize="xs" overflowX="auto" whiteSpace="pre">
{`// GET /stations/2/data/historical/20250501/20250610
{
  "station_id": 2,
  "period": {
    "start": "2025-05-01",
    "end": "2025-06-10",
    "days": 40,
    "grouping": "1day"
  },
  "count": 40,
  "data": [
    {
      "temp_min": 16.8,
      "temp_avg": 22.4,
      "temp_max": 27.3,
      "humidity_min": 58,
      "humidity_avg": 82,
      "humidity_max": 97,
      "heat_index_min": 16.8,
      "heat_index_avg": 22.9,
      "heat_index_max": 29.5,
      "wind_chill_min": 15.2,
      "wind_chill_avg": 22.1,
      "wind_chill_max": 27.3,
      "dew_point_min": 14.6,
      "dew_point_avg": 19.8,
      "dew_point_max": 23.5,
      "pressure_min": 958.4,
      "pressure_avg": 963.1,
      "pressure_max": 967.8,
      "wind_speed_max": 18.2,
      "wind_dir_avg": 175,
      "wind_gust_max": 28.4,
      "precip_rate_max": 8.6,
      "precip_total": 12.4,
      "uv_max": 8,
      "solar_radiation_max": 820.5,
      "period": "2025-05-01T00:00:00-03:00"
    },
    ...
  ]
}`}
                        </Box>
                    </EndpointSection>
                </Box>
            </Box>

            {/* Footer */}
            <Flex justify="center" mb={8}>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                    Dúvidas? Entre em contato com a equipe do WS Clima.
                    <br />
                    <Link
                        href="https://wa.me/553591851089?text=Ol%C3%A1%21%20Tenho%20uma%20d%C3%BAvida%20sobre%20a%20API%20de%20integra%C3%A7%C3%A3o%20do%20WS%20Clima"
                        isExternal
                        color="whatsapp.500"
                        fontWeight="semibold"
                    >
                        <Flex align="center" justify="center" gap={1} mt={1}>
                            <Icon as={FaWhatsapp} /> William — (35) 9185-1089
                        </Flex>
                    </Link>
                </Text>
            </Flex>
        </Box>
    );
}

export default IntegrationDocs;
